import { useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/Avatar";
import { formatDate } from "@/lib/helpers";
import { NoteBlank, PaperPlaneTilt, Robot, ArrowDown, Check, Trash } from "@phosphor-icons/react";

/**
 * Resolve the message's sender_kind following the Phase 3.3 taxonomy with
 * legacy fallbacks:
 *   1. message.sender_kind        (Phase 3.3 canonical)
 *   2. message.sender_role        (Phase 3.2 legacy: client/hub_agent/kimkim_agent/system)
 *   3. message.sender_type + heuristics  (very old rows before Phase 3.2)
 *
 * Returns "customer" | "agent_hub" | "agent_kimkim" | "system".
 */
function resolveKind(message) {
    const k = message.sender_kind;
    if (k === "customer" || k === "agent_hub" || k === "agent_kimkim") return k;
    const role = message.sender_role;
    if (role === "client") return "customer";
    if (role === "hub_agent") return "agent_hub";
    if (role === "kimkim_agent") return "agent_kimkim";
    if (role === "system") return "system";
    // Very old rows: infer from sender_type + presence of sender_user_id.
    if (message.sender_type === "system") return "system";
    if (message.sender_type === "customer") return "customer";
    if (message.sender_type === "agent") {
        return message.sender_user_id ? "agent_hub" : "agent_kimkim";
    }
    return "customer";
}

function Bubble({ message, customer, agent, onDelete }) {
    const kind = resolveKind(message);

    if (kind === "system") {
        return (
            <div className="self-center w-full text-[11px] font-medium text-slate-500 uppercase tracking-wider my-4
                            flex items-center gap-2 before:h-px before:bg-slate-200 before:flex-1 after:h-px after:bg-slate-200 after:flex-1"
                 data-testid={`thread-message-${message._id}`}>
                <Robot size={12} /> {message.body}
                {onDelete && (
                    <button
                        type="button"
                        onClick={() => onDelete(message)}
                        className="opacity-0 group-hover:opacity-100 max-md:opacity-70 hover:text-rose-500 transition-opacity"
                        aria-label="Borrar mensaje"
                        data-testid={`thread-message-delete-${message._id}`}
                    >
                        <Trash size={12} />
                    </button>
                )}
            </div>
        );
    }

    // Phase 3.3 layout rules (confirmed by Eduardo):
    //   customer     → LEFT, grey bubble, name above.
    //   agent_hub    → RIGHT, brand-blue bubble, "<name> · Hub" above + ✓ check inside.
    //   agent_kimkim → RIGHT, brand-blue bubble, "<name> · vía KimKim" above with
    //                  a distinctive amber "vía KimKim" pill so the agent knows
    //                  the message wasn't authored through this Hub.
    const displayName = message.sender_name
        || (kind === "customer" ? customer?.name : agent?.name)
        || (kind === "agent_kimkim" ? "Espíritu Travel" : "Desconocido");

    const isRight = kind !== "customer";
    const avatarColor = kind === "agent_hub" ? agent?.avatar_color : undefined;

    const bubbleClass = {
        customer:     "bg-slate-100 text-slate-800 rounded-2xl rounded-tl-sm",
        agent_hub:    "bg-brand-600 text-white rounded-2xl rounded-tr-sm",
        agent_kimkim: "bg-brand-600 text-white rounded-2xl rounded-tr-sm",
    }[kind];

    const kimkimBadge = kind === "agent_kimkim"
        ? { label: "vía KimKim", cls: "bg-orange-100 text-orange-800 border-orange-300" }
        : null;

    // Sub-label under the name gives the source at a glance ("Hub" or "vía KimKim").
    const sourceLabel = kind === "agent_hub"
        ? "Hub"
        : (kind === "agent_kimkim" ? "vía KimKim" : null);

    return (
        <div className={`group flex gap-2 items-end ${isRight ? "flex-row-reverse" : "flex-row"}`}
             data-testid={`thread-message-${message._id}`}
             data-sender-kind={kind}
             data-sender-role={message.sender_role || ""}>
            <Avatar name={displayName} color={avatarColor} size={28} />
            <div className={`max-w-[80%] max-md:max-w-[70%] ${isRight ? "items-end" : "items-start"} flex flex-col gap-1`}>
                <div className={`flex items-center gap-1.5 px-1 ${isRight ? "flex-row-reverse" : ""}`}>
                    <span className="text-[11px] font-semibold text-slate-700" data-testid="bubble-sender-name">
                        {displayName}{sourceLabel ? ` · ${sourceLabel}` : ""}
                    </span>
                    {kimkimBadge && (
                        <span className={`text-[9px] font-medium uppercase tracking-wide border rounded-full px-1.5 py-px ${kimkimBadge.cls}`}
                              data-testid="bubble-channel-badge">
                            {kimkimBadge.label}
                        </span>
                    )}
                </div>
                <div className={`${bubbleClass} px-4 py-2.5 shadow-sm relative`}>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.body}</p>
                    {kind === "agent_hub" && (
                        <span
                            className="absolute bottom-1 right-2 inline-flex items-center text-[10px] text-brand-100/80"
                            title="Enviado desde el Hub"
                            data-testid="bubble-hub-check"
                        >
                            <Check size={11} weight="bold" />
                        </span>
                    )}
                </div>
                <span className="text-[10px] text-slate-400 px-1">
                    {formatDate(message.created_at)}
                </span>
            </div>
            {onDelete && (
                <button
                    type="button"
                    onClick={() => onDelete(message)}
                    className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100
                               max-md:opacity-70
                               transition-opacity text-slate-400 hover:text-rose-600 active:text-rose-700
                               p-1.5 rounded-md hover:bg-rose-50 active:bg-rose-100 self-center shrink-0"
                    aria-label="Borrar mensaje"
                    title="Borrar mensaje"
                    data-testid={`thread-message-delete-${message._id}`}
                >
                    <Trash size={14} weight="bold" />
                </button>
            )}
        </div>
    );
}

function NoteBubble({ note, author }) {
    return (
        <div className="self-center w-[95%] bg-yellow-50 border-l-4 border-yellow-300 text-amber-900
                        rounded-r-lg rounded-l-sm px-4 py-2.5 flex items-start gap-2"
             data-testid={`thread-note-${note._id}`}>
            <NoteBlank size={16} className="mt-0.5 text-yellow-700"/>
            <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-0.5">
                    Nota interna · {author?.name || "—"} · {formatDate(note.created_at)}
                </div>
                <p className="whitespace-pre-wrap text-sm">{note.body}</p>
            </div>
        </div>
    );
}

/**
 * Traditional chat layout (WhatsApp / Slack / iMessage style):
 *   - Messages sorted OLDEST → NEWEST (top → bottom)
 *   - Composer lives BELOW this component (fixed bottom in InboxPage)
 *   - On mount / conversation change: auto-scroll to the bottom so the latest
 *     message is visible.
 *   - When new items arrive AND the user is already glued to the bottom:
 *     stay glued (auto-scroll).
 *   - When new items arrive AND the user has scrolled up to read older
 *     messages: surface a "N mensajes nuevos ↓" badge that jumps to the
 *     bottom on click.
 *
 * Reverted from the inverted-Gmail-style layout after agent feedback.
 */
export default function Thread({ messages = [], notes = [], customer, usersById = {}, onDeleteMessage }) {
    const scrollRef = useRef(null);
    const prevCountRef = useRef(0);
    const [unseenCount, setUnseenCount] = useState(0);

    // ASCENDING: oldest first, newest last.
    const items = [
        ...messages.map(m => ({ kind: "msg", item: m, ts: new Date(m.created_at).getTime() })),
        ...notes.map(n => ({ kind: "note", item: n, ts: new Date(n.created_at).getTime() })),
    ].sort((a, b) => a.ts - b.ts);

    const totalCount = items.length;
    const convKey = messages[0]?.conversation_id || notes[0]?.conversation_id || "";

    const isAtBottom = () => {
        const el = scrollRef.current;
        if (!el) return true;
        // 40px of slack — anything within is "at bottom"
        return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    };

    const scrollToBottom = (behavior = "smooth") => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior });
        setUnseenCount(0);
    };

    // On conversation change: jump to bottom instantly
    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        prevCountRef.current = totalCount;
        setUnseenCount(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convKey]);

    // On new items: if at bottom, stay glued; if scrolled up, increment badge
    useEffect(() => {
        const prev = prevCountRef.current;
        if (totalCount > prev) {
            const added = totalCount - prev;
            if (isAtBottom()) {
                requestAnimationFrame(() => {
                    const el = scrollRef.current;
                    if (el) el.scrollTop = el.scrollHeight;
                });
            } else {
                setUnseenCount(c => c + added);
            }
        }
        prevCountRef.current = totalCount;
    }, [totalCount]);

    const onScroll = () => {
        if (isAtBottom() && unseenCount > 0) setUnseenCount(0);
    };

    if (items.length === 0) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-6"
                 data-testid="thread-empty">
                <PaperPlaneTilt size={36} className="mb-2"/>
                Sin mensajes todavía.
            </div>
        );
    }

    return (
        <div className="flex-1 relative min-h-0">
            <div ref={scrollRef}
                 onScroll={onScroll}
                 className="absolute inset-0 overflow-y-auto vh-scroll px-4 py-6 flex flex-col gap-3 bg-slate-50"
                 data-testid="thread-scroll">
                {items.map(({ kind, item }) => {
                    if (kind === "msg") {
                        const agent = item.sender_user_id ? usersById[item.sender_user_id] : null;
                        return (
                            <Bubble
                                key={item._id}
                                message={item}
                                customer={customer}
                                agent={agent}
                                onDelete={onDeleteMessage}
                            />
                        );
                    }
                    const author = usersById[item.author_user_id];
                    return <NoteBubble key={item._id} note={item} author={author} />;
                })}
            </div>

            {unseenCount > 0 && (
                <button
                    type="button"
                    onClick={() => scrollToBottom("smooth")}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5
                               bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold
                               px-3 py-1.5 rounded-full shadow-md transition-colors"
                    data-testid="thread-new-messages-badge"
                >
                    <ArrowDown size={12} weight="bold" />
                    {unseenCount} mensaje{unseenCount === 1 ? "" : "s"} nuevo{unseenCount === 1 ? "" : "s"} · Ir abajo
                </button>
            )}
        </div>
    );
}
